import { useState, useMemo, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  InputAdornment,
  Chip,
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme,
  Divider,
  Alert,
} from '@mui/material'
import {
  Search as SearchIcon,
  Menu as MenuIcon,
  ChevronRight as ChevronRightIcon,
  Help as HelpIcon,
} from '@mui/icons-material'
import Fuse from 'fuse.js'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import helpArticles from '../data/helpArticles.json'

interface HelpArticle {
  id: string
  category: string
  title: string
  keywords: string[]
  content: string
  lastUpdated: string
}

interface Category {
  id: string
  name: string
  icon?: string
}

const categories: Category[] = [
  { id: 'getting-started', name: 'Getting Started' },
  { id: 'sales-leads', name: 'Sales & Leads' },
  { id: 'clients-onboarding', name: 'Clients & Onboarding' },
  { id: 'tasks-communication', name: 'Tasks & Communication' },
  { id: 'resumes-linkedin', name: 'Resumes & LinkedIn' },
  { id: 'interview-prep', name: 'Interview Preparation' },
  { id: 'admin-config', name: 'Admin & Configuration' },
  { id: 'feedback-bugs', name: 'Reporting Issues & Features' },
  { id: 'common-features', name: 'Common Features' },
  { id: 'troubleshooting', name: 'Troubleshooting' },
]

const DRAWER_WIDTH = 280

export default function Help() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [selectedArticle, setSelectedArticle] = useState<HelpArticle | null>(null)
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)

  // Initialize Fuse.js for fuzzy search
  const fuse = useMemo(
    () =>
      new Fuse(helpArticles as HelpArticle[], {
        keys: ['title', 'keywords', 'content', 'category'],
        threshold: 0.3,
        includeScore: true,
      }),
    []
  )

  // Filter articles based on search and category
  const filteredArticles = useMemo(() => {
    let results: HelpArticle[] = helpArticles as HelpArticle[]

    // Apply search filter
    if (searchQuery.trim()) {
      const fuseResults = fuse.search(searchQuery)
      results = fuseResults.map((result) => result.item)
    }

    // Apply category filter
    if (selectedCategory) {
      results = results.filter((article) => article.category === selectedCategory)
    }

    return results
  }, [searchQuery, selectedCategory, fuse])

  // Auto-select first article when filtered results change
  useEffect(() => {
    if (filteredArticles.length > 0 && !selectedArticle) {
      setSelectedArticle(filteredArticles[0])
    }
  }, [filteredArticles, selectedArticle])

  // Get article count per category
  const categoryArticleCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    ;(helpArticles as HelpArticle[]).forEach((article) => {
      counts[article.category] = (counts[article.category] || 0) + 1
    })
    return counts
  }, [])

  const handleCategoryClick = (categoryId: string) => {
    if (selectedCategory === categoryId) {
      setSelectedCategory(null)
    } else {
      setSelectedCategory(categoryId)
    }
    setSelectedArticle(null)
    if (isMobile) {
      setMobileDrawerOpen(false)
    }
  }

  const handleArticleClick = (article: HelpArticle) => {
    setSelectedArticle(article)
    if (isMobile) {
      setMobileDrawerOpen(false)
    }
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
    setSelectedArticle(null)
  }

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Search Bar */}
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search help articles..."
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
              },
            },
            '& .MuiInputBase-input': {
              color: 'white',
            },
            '& .MuiInputBase-input::placeholder': {
              color: 'rgba(255, 255, 255, 0.7)',
              opacity: 1,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'white' }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Divider />

      {/* Categories */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <List>
          {categories.map((category) => {
            const count = categoryArticleCounts[category.id] || 0
            const isSelected = selectedCategory === category.id

            return (
              <ListItem key={category.id} disablePadding>
                <ListItemButton
                  selected={isSelected}
                  onClick={() => handleCategoryClick(category.id)}
                  sx={{
                    borderLeft: isSelected ? `4px solid ${theme.palette.primary.main}` : 'none',
                    pl: isSelected ? 1.5 : 2,
                  }}
                >
                  <ListItemText
                    primary={category.name}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: isSelected ? 600 : 400,
                    }}
                  />
                  <Chip label={count} size="small" sx={{ ml: 1 }} />
                </ListItemButton>
              </ListItem>
            )
          })}
        </List>
      </Box>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Desktop Drawer */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              position: 'relative',
              height: '100%',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Main Content Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {isMobile && (
            <IconButton onClick={() => setMobileDrawerOpen(true)} edge="start">
              <MenuIcon />
            </IconButton>
          )}
          <HelpIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Help Center
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          {filteredArticles.length === 0 ? (
            <Alert severity="info">
              No articles found. Try adjusting your search or category filter.
            </Alert>
          ) : selectedArticle ? (
            /* Article Detail View */
            <Card>
              <CardContent>
                {/* Article Header */}
                <Box sx={{ mb: 3 }}>
                  <Chip
                    label={
                      categories.find((c) => c.id === selectedArticle.category)?.name ||
                      selectedArticle.category
                    }
                    size="small"
                    color="primary"
                    sx={{ mb: 2 }}
                  />
                  <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                    {selectedArticle.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Last updated: {new Date(selectedArticle.lastUpdated).toLocaleDateString()}
                  </Typography>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Article Content */}
                <Box
                  sx={{
                    '& h1': { fontSize: '2rem', fontWeight: 600, mt: 3, mb: 2 },
                    '& h2': { fontSize: '1.5rem', fontWeight: 600, mt: 3, mb: 2 },
                    '& h3': { fontSize: '1.25rem', fontWeight: 600, mt: 2, mb: 1 },
                    '& p': { mb: 2, lineHeight: 1.7 },
                    '& ul, & ol': { mb: 2, pl: 3 },
                    '& li': { mb: 1 },
                    '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1, my: 2 },
                    '& code': {
                      backgroundColor: theme.palette.grey[100],
                      padding: '2px 6px',
                      borderRadius: 1,
                      fontSize: '0.9em',
                    },
                    '& pre': {
                      backgroundColor: theme.palette.grey[100],
                      padding: 2,
                      borderRadius: 1,
                      overflow: 'auto',
                    },
                    '& blockquote': {
                      borderLeft: `4px solid ${theme.palette.primary.main}`,
                      pl: 2,
                      ml: 0,
                      fontStyle: 'italic',
                      color: theme.palette.text.secondary,
                    },
                  }}
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {selectedArticle.content}
                  </ReactMarkdown>
                </Box>
              </CardContent>
            </Card>
          ) : (
            /* Article List View */
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                {searchQuery
                  ? `Search Results (${filteredArticles.length})`
                  : selectedCategory
                  ? `${categories.find((c) => c.id === selectedCategory)?.name} (${
                      filteredArticles.length
                    })`
                  : `All Articles (${filteredArticles.length})`}
              </Typography>

              <List>
                {filteredArticles.map((article) => (
                  <Card key={article.id} sx={{ mb: 2 }}>
                    <ListItemButton onClick={() => handleArticleClick(article)}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                              {article.title}
                            </Typography>
                            <ChevronRightIcon fontSize="small" />
                          </Box>
                        }
                        secondary={
                          <Chip
                            label={
                              categories.find((c) => c.id === article.category)?.name ||
                              article.category
                            }
                            size="small"
                            sx={{ mt: 1 }}
                          />
                        }
                      />
                    </ListItemButton>
                  </Card>
                ))}
              </List>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}
